import { useEffect } from "react";

interface AutoLoadUntilScrollableParams {
  containerId: string;
  triggerLoad: () => void;
  isLastPage?: boolean;
  hasError?: boolean;
  isValidating?: boolean;
  dependency?: unknown;
}

/**
 * Automatically triggers data load if content height is less than viewport height.
 * Stops when scroll appears or all pages are loaded.
 *
 * @param {Object} params
 * @param {string} params.containerId - ID of the scrollable content wrapper.
 * @param {Function} params.triggerLoad - Callback to load next page.
 * @param {boolean} params.isLastPage - True if all data is loaded.
 * @param {any} params.dependency - Triggers recheck when this changes (e.g., campaign count).
 */
const useAutoLoadUntilScrollable = ({
  containerId,
  triggerLoad,
  isLastPage,
  hasError,
  isValidating,
  dependency,
}: AutoLoadUntilScrollableParams): void => {
  useEffect(() => {
    const el = document.getElementById(containerId);
    if (!el || isLastPage || hasError || isValidating) return;

    let frameId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const checkAndLoad = () => {
      const contentHeight = el.scrollHeight;
      const viewportHeight = window.innerHeight;

      if (contentHeight <= viewportHeight) {
        triggerLoad();
      }
    };

    frameId = requestAnimationFrame(() => {
      timeoutId = setTimeout(checkAndLoad, 100);
    });

    window.addEventListener("resize", checkAndLoad);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("resize", checkAndLoad);
    };
  }, [
    containerId,
    dependency,
    isLastPage,
    hasError,
    isValidating,
    triggerLoad,
  ]);
};

export default useAutoLoadUntilScrollable;
